/** Base: !（U+0021）から~（U+007E）まで, katakana: カタカナ, symbol: 特殊記号 */
type ConvertTargetType = 'alphabet' | 'number' | 'katakana' | 'symbol'

const KATAKANA_MAP = {
  ｶﾞ: 'ガ',
  ｷﾞ: 'ギ',
  ｸﾞ: 'グ',
  ｹﾞ: 'ゲ',
  ｺﾞ: 'ゴ',
  ｻﾞ: 'ザ',
  ｼﾞ: 'ジ',
  ｽﾞ: 'ズ',
  ｾﾞ: 'ゼ',
  ｿﾞ: 'ゾ',
  ﾀﾞ: 'ダ',
  ﾁﾞ: 'ヂ',
  ﾂﾞ: 'ヅ',
  ﾃﾞ: 'デ',
  ﾄﾞ: 'ド',
  ﾊﾞ: 'バ',
  ﾋﾞ: 'ビ',
  ﾌﾞ: 'ブ',
  ﾍﾞ: 'ベ',
  ﾎﾞ: 'ボ',
  ﾊﾟ: 'パ',
  ﾋﾟ: 'ピ',
  ﾌﾟ: 'プ',
  ﾍﾟ: 'ペ',
  ﾎﾟ: 'ポ',
  ｳﾞ: 'ヴ',
  ﾜﾞ: 'ヷ',
  ｦﾞ: 'ヺ',
  ｧ: 'ァ',
  ｨ: 'ィ',
  ｩ: 'ゥ',
  ｪ: 'ェ',
  ｫ: 'ォ',
  ｱ: 'ア',
  ｲ: 'イ',
  ｳ: 'ウ',
  ｴ: 'エ',
  ｵ: 'オ',
  ｶ: 'カ',
  ｷ: 'キ',
  ｸ: 'ク',
  ｹ: 'ケ',
  ｺ: 'コ',
  ｻ: 'サ',
  ｼ: 'シ',
  ｽ: 'ス',
  ｾ: 'セ',
  ｿ: 'ソ',
  ｯ: 'ッ',
  ﾀ: 'タ',
  ﾁ: 'チ',
  ﾂ: 'ツ',
  ﾃ: 'テ',
  ﾄ: 'ト',
  ﾅ: 'ナ',
  ﾆ: 'ニ',
  ﾇ: 'ヌ',
  ﾈ: 'ネ',
  ﾉ: 'ノ',
  ﾊ: 'ハ',
  ﾋ: 'ヒ',
  ﾌ: 'フ',
  ﾍ: 'ヘ',
  ﾎ: 'ホ',
  ﾏ: 'マ',
  ﾐ: 'ミ',
  ﾑ: 'ム',
  ﾒ: 'メ',
  ﾓ: 'モ',
  ｬ: 'ャ',
  ｭ: 'ュ',
  ｮ: 'ョ',
  ﾔ: 'ヤ',
  ﾕ: 'ユ',
  ﾖ: 'ヨ',
  ﾗ: 'ラ',
  ﾘ: 'リ',
  ﾙ: 'ル',
  ﾚ: 'レ',
  ﾛ: 'ロ',
  ﾜ: 'ワ',
  ｦ: 'ヲ',
  ﾝ: 'ン',
} as const

const SYMBOL_MAP = {
  '｡': '。',
  '｢': '「',
  '｣': '」',
  '､': '、',
  '･': '・',
  '-': 'ー',
  _: '＿',
  ｰ: 'ー',
  '゛': 'ﾞ',
  '゜': 'ﾟ',
  ' ': '　',
} as const

export const useTextWidthConvertor = () => {
  /** カタカナを全角に変換 */
  const toFullKana = (str: string) => {
    const regex = new RegExp(`(${Object.keys(KATAKANA_MAP).join('|')})`, 'g')
    return str.replace(regex, (half) => {
      return KATAKANA_MAP[half as keyof typeof KATAKANA_MAP]
    })
  }

  /** カタカナを半角に変換 */
  const toHalfKana = (str: string) => {
    const reverseKatakanaMap = Object.entries(KATAKANA_MAP).reduce(
      (prev, next) => ({ ...prev, [next[1]]: [next[0]] }),
      {}
    )
    const regex = new RegExp(`(${Object.values(KATAKANA_MAP).join('|')})`, 'g')
    return str.replace(regex, (full) => {
      return reverseKatakanaMap[full as keyof typeof reverseKatakanaMap]
    })
  }

  /** カタカナを全角に変換 */
  const toFullSymbol = (str: string) => {
    const regex = new RegExp(`(${Object.keys(SYMBOL_MAP).join('|')})`, 'g')
    return str.replace(
      regex,
      (half) => SYMBOL_MAP[half as keyof typeof SYMBOL_MAP]
    )
  }

  /** カタカナを半角に変換 */
  const toHalfSymbol = (str: string) => {
    const reverseSymbolMap = Object.entries(SYMBOL_MAP).reduce(
      (prev, next) => ({ ...prev, [next[1]]: [next[0]] }),
      {}
    )
    const regex = new RegExp(
      `(${Object.keys(reverseSymbolMap).join('|')})`,
      'g'
    )
    return str.replace(regex, (full) => {
      return reverseSymbolMap[full as keyof typeof reverseSymbolMap]
    })
  }

  /** 半角文字までを全角に変換 */
  const toFullAlphabet = (str: string) => {
    return str.replace(/[\x21-\x7E]/g, (s) => {
      return String.fromCharCode(s.charCodeAt(0) + 0xfee0)
    })
  }

  /** !（U+0021）から~（U+007E）までを半角に変換 */
  const toHalfAlphabet = (str: string) => {
    return str.replace(/[\uFF21-\uFF5E]/g, (s) => {
      return String.fromCharCode(s.charCodeAt(0) - 0xfee0)
    })
  }

  /** 半角文字までを全角に変換 */
  const toFullNumber = (str: string) => {
    return str.replace(/[0-9]/g, (s) => {
      return String.fromCharCode(s.charCodeAt(0) + 0xfee0)
    })
  }

  /** !（U+0021）から~（U+007E）までを半角に変換 */
  const toHalfNumber = (str: string) => {
    return str.replace(/[０-９]/g, (s) => {
      return String.fromCharCode(s.charCodeAt(0) - 0xfee0)
    })
  }

  const toFullWidth = (
    str: string,
    types: ConvertTargetType[] = ['alphabet', 'katakana', 'number', 'symbol']
  ) => {
    return types.reduce((prev, next) => {
      switch (next) {
        case 'number': {
          return toFullNumber(prev)
        }
        case 'alphabet': {
          return toFullAlphabet(prev)
        }
        case 'katakana': {
          return toFullKana(prev)
        }
        case 'symbol': {
          return toFullSymbol(prev)
        }
      }
    }, str)
  }

  const toHalfWidth = (str: string, types: ConvertTargetType[]) => {
    return types.reduce((prev, next) => {
      switch (next) {
        case 'number': {
          return toHalfNumber(prev)
        }
        case 'alphabet': {
          return toHalfAlphabet(prev)
        }
        case 'katakana': {
          return toHalfKana(prev)
        }
        case 'symbol': {
          return toHalfSymbol(prev)
        }
      }
    }, str)
  }

  return {
    toFullWidth,
    toHalfWidth,
  }
}
